/**
 * The client AI terms a client team accepts in the app before using the studios (cowork GET /v1/me/terms,
 * POST /v1/me/terms/accept). Staff never see them.
 *
 * PLACEHOLDER copy: legal owns the final summary and the full terms page (CLIENT_AI_TERMS_URL in config/legal.ts).
 * Keep it plain and short, no dashes.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coworkApi, type ClientTermsState } from '@/cowork/omniad/api/coworkApi';
import { COWORK_ME_KEY } from './useCoworkMe';

/** The version the API expects today; the server decides what is current (GET /v1/me/terms `version`). */
export const CLIENT_AI_TERMS_VERSION = 'client-ai-terms-2026-11';

export const CLIENT_AI_TERMS_TITLE = 'Before you use the studios';

export const CLIENT_AI_TERMS_SUMMARY: readonly string[] = [
  'The studios use AI to help you make decks, music, voice and video for your brand.',
  'Check what the AI makes before you share or publish it. Your team decides what goes out.',
  'AI audio and video carries a "Made with AI" label. Keep it when you publish.',
  'Creator clips can only be used within the usage rights of their campaign.',
  'Only use creator content in ads when its licence is confirmed by Beatly and your team.',
  'Each studio has a monthly limit for your team.',
];

export const CLIENT_TERMS_KEY = ['cowork', 'terms'] as const;

/** Does this answer mean the person still has to accept? Required (a client) and not accepted for this version. */
export function termsNeedAcceptance(t: ClientTermsState | null | undefined): boolean {
  return !!t && t.required === true && !t.acceptedAt;
}

/**
 * The terms state for a client session. `enabled` false for staff (and while /v1/me loads). Fails closed: when
 * GET /v1/me/terms cannot be read (an API without the route, a 5xx, the network) the status is `error`, the studios
 * stay shut and the page offers to try again (`retry`). Only an answer that says accepted (or not required) opens them.
 *
 * `termsPending` is GET /v1/me's `termsPending` (asked every minute for a client): true when the API enforces the terms
 * and this user has not accepted the current version. It opens the modal on its own, even while a cached terms answer
 * still says accepted (a new version, or terms enforced since the page loaded), and asks GET /v1/me/terms again for
 * the version to accept. Once accepted here, /v1/me is asked again so the flag clears; until that newer answer arrives
 * (`termsPendingAt`, the /v1/me answer's time) the old flag no longer counts.
 */
export function useClientTerms(enabled: boolean, termsPending?: boolean, termsPendingAt = 0) {
  const qc = useQueryClient();
  const [acceptedHereAt, setAcceptedHereAt] = useState(0);
  const q = useQuery<ClientTermsState>({ queryKey: CLIENT_TERMS_KEY, queryFn: () => coworkApi.launch.terms(), enabled, retry: 1, staleTime: 10 * 60_000 });
  const accept = useMutation({
    mutationFn: (version: string) => coworkApi.launch.acceptTerms(version),
    onSuccess: (r, version) => {
      setAcceptedHereAt(Date.now());
      qc.setQueryData<ClientTermsState>(CLIENT_TERMS_KEY, (prev) => ({ version: prev?.version ?? version, required: prev?.required ?? true, acceptedAt: r.acceptedAt || new Date().toISOString() }));
      void qc.invalidateQueries({ queryKey: COWORK_ME_KEY });
    },
  });
  // Accepted in this session: a /v1/me flag from before that is stale, so it no longer counts.
  const pending = enabled && termsPending === true && !(acceptedHereAt > 0 && termsPendingAt <= acceptedHereAt);
  const cachedAccepted = !!q.data && !termsNeedAcceptance(q.data);
  useEffect(() => {
    if (pending && cachedAccepted) void qc.invalidateQueries({ queryKey: CLIENT_TERMS_KEY });
  }, [pending, cachedAccepted, qc]);
  return { status: clientTermsStatus(enabled, q, pending), version: q.data?.version || CLIENT_AI_TERMS_VERSION, accept, retry: () => { void q.refetch(); } };
}

export type ClientTermsStatus = 'off' | 'loading' | 'needed' | 'accepted' | 'error';

/**
 * What the terms query means for the studios. A cached answer wins over a failed refetch (accepted stays accepted);
 * with no answer at all, an error is `error` (closed), never `accepted`.
 */
export function clientTermsStatus(enabled: boolean, q: { data?: ClientTermsState; isPending: boolean; isError: boolean; isFetching?: boolean }, termsPending = false): ClientTermsStatus {
  if (!enabled) return 'off';
  // GET /v1/me says the API wants the terms accepted: the modal opens whatever an older terms answer said.
  if (termsPending) return 'needed';
  if (q.data) return termsNeedAcceptance(q.data) ? 'needed' : 'accepted';
  if (q.isError) return q.isFetching ? 'loading' : 'error';
  return 'loading';
}
